import {
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import { map } from 'rxjs/operators';
import { PracticeSetupStepValidation } from './practice-setup-step-validation';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
} from './practice-setup-step.types';
import {
  initRawSchema,
  toListContent,
  toParagraphContent,
} from '@principle-theorem/editor';

const reviewCount = 10;

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/brand/{brandId}/charting/treatments'],
  validationMode: 'count',
  requiredCount: reviewCount,
};

export const treatmentsSetupStep: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.TreatmentsSetup,
  category: PracticeSetupStepCategory.ConditionsAndTreatments,
  title: 'Review and Configure Treatments',
  description: initRawSchema([
    toParagraphContent(
      `Treatments setup is perhaps the most important stage of your workspace setup. Your Principle workspace comes with a set of comprehensive treatments, however you may want to add any that may be missing, or remove any that you don't need.`
    ),
    toParagraphContent(
      `If migrating from an Existing PMS, consider which treatments have been performed in the past, and what they will be "mapped" to in Principle.`
    ),
    toParagraphContent(
      `Within each Treatment it is essential that the following settings are checked and configured to your practices' standards:`
    ),
    toListContent([
      `General Settings: Name, Category and Duration of Treatment`,
      `Charting Options: If applicable, how the treatment is displayed on a patient's Odontogram when charted.`,
      `Conditions: Optional links to conditions which this treatment is used to treat`,
      `Service codes: Which service codes are always billed, and optionally billed, when this treatment is performed.`,
    ]),
    toParagraphContent(`(Post Migration)`),
    toListContent([
      `Packages: Create any deals or special pricing options for the Treatment`,
      `Automations: Any specific automated messages, forms or tasks that are generated by this Treatment`,
      `Pre & Post Appointment Checklists Items: Any reminders that Front of House should be aware of in regards to this Treatment when checking patients in or out.`,
    ]),
  ]),
  dependsOn: [
    PracticeSetupStepId.TreatmentCategories,
    PracticeSetupStepId.ConditionsSetup,
  ],
  getRoute: (brand, _practice) =>
    `settings/brand/${brand.ref.id}/charting/treatments`,
  validate$: (practice$, step, options) => {
    return practice$.pipe(
      map((_practice) => {
        if (options?.reviewed) {
          return { valid: true };
        }

        const pageVisitValidation =
          PracticeSetupStepValidation.validatePageVisits(
            step,
            pageVisitTracking
          );
        if (pageVisitValidation.valid) {
          return { valid: true };
        }

        return {
          valid: false,
          reason: `Please review and approve at least ${reviewCount} treatments before completing this step`,
        };
      })
    );
  },
  pageVisitTracking,
};
